import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listAllOrgs, formatCAD, type AdminOrgRow } from '@/lib/adminBilling';
import { humanize } from '@/lib/errors';
import { formatDate } from '@/lib/dates';

/**
 * /admin/orgs — searchable list of all organizations on the platform.
 */
export default function OrgsList() {
  const [rows, setRows] = useState<AdminOrgRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    let cancelled = false;
    listAllOrgs()
      .then((r) => { if (!cancelled) { setRows(r); setLoading(false); } })
      .catch((err) => { if (!cancelled) { setError(humanize(err)); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      r.display_name?.toLowerCase().includes(needle)
      || r.legal_name?.toLowerCase().includes(needle)
      || r.primary_contact_email?.toLowerCase().includes(needle)
    );
  }, [rows, q]);

  if (loading) return <p className="text-sm text-muted-foreground">Loading organizations…</p>;
  if (error)   return <p className="text-sm text-red-600">{error}</p>;

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Organizations</h2>
          <p className="text-sm text-muted-foreground">
            {rows.length} org{rows.length === 1 ? '' : 's'}. Click into any row for details.
          </p>
        </div>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, legal name, or contact email"
          className="rounded-md border border-border px-3 py-1.5 text-sm w-72 max-w-full"
        />
      </header>

      <div className="rounded-lg border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-border text-left">
              <Th>Organization</Th>
              <Th className="hidden md:table-cell">Owner email</Th>
              <Th className="hidden md:table-cell">Signed up</Th>
              <Th className="hidden md:table-cell text-right">Locations</Th>
              <Th className="hidden md:table-cell text-right">Staff</Th>
              <Th>Plan status</Th>
              <Th>Tier</Th>
              <Th className="hidden md:table-cell text-right">MRR (est.)</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr
                key={r.organization_id}
                className={`border-b border-border ${i % 2 === 0 ? '' : 'bg-muted/10'} hover:bg-muted/30`}
              >
                <Td>
                  <Link to={`/admin/orgs/${r.organization_id}`} className="hover:underline" style={{ color: 'hsl(var(--brand-navy))' }}>
                    {r.display_name}
                  </Link>
                  <div className="text-xs text-muted-foreground">{r.legal_name}</div>
                </Td>
                <Td className="hidden md:table-cell text-xs">{r.primary_contact_email ?? '—'}</Td>
                <Td className="hidden md:table-cell text-xs">{formatDate(r.org_created_at)}</Td>
                <Td className="hidden md:table-cell text-right">{r.active_location_count} / {r.location_count}</Td>
                <Td className="hidden md:table-cell text-right">{r.staff_count}</Td>
                <Td><StatusChip status={r.sub_status} /></Td>
                <Td>{tierLabel(r.sub_tier)}</Td>
                <Td className="hidden md:table-cell text-right">{formatCAD(Number(r.mrr_cad))}</Td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-sm text-muted-foreground">No orgs match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <th scope="col" className={`p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium ${className}`}>
      {children}
    </th>
  );
}
function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`p-3 align-top ${className}`}>{children}</td>;
}

function StatusChip({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs text-muted-foreground">—</span>;
  const tones: Record<string, string> = {
    trial:    'bg-blue-50 text-blue-900 border-blue-200',
    pilot:    'bg-emerald-50 text-emerald-900 border-emerald-200',
    active:   'bg-emerald-50 text-emerald-900 border-emerald-200',
    past_due: 'bg-red-50 text-red-900 border-red-200',
    canceled: 'bg-muted/40 text-muted-foreground border-border',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${tones[status] ?? ''}`}>
      {status}
    </span>
  );
}
function tierLabel(t: string | null) {
  if (!t) return <span className="text-xs text-muted-foreground">—</span>;
  return <span className="text-xs">{t[0].toUpperCase() + t.slice(1)}</span>;
}
